class PDPInventoryChecker {
  constructor() {
    this.inventoryData = null;
    this.isTodaySelected = false;
    this.selectedLocationId = null;
    this.isLoading = false;
    this.currentVariantId = null;
    this.API_URL = window.EveryTable?.api?.baseUrl || "https://admin-app.everytable-sh.com/api";
    this.init();
  }

  init() {
    // Get current variant ID from URL
    this.getCurrentVariantId();
    
    // Check if today's date is selected
    this.checkIfTodaySelected();
    
    // Listen for date changes
    this.listenForDateChanges();
    
    // Listen for location changes
    this.listenForLocationChanges();
    
    // Listen for variant changes
    this.listenForVariantChanges();
    
    // Initial check
    this.updateInventoryDisplay();
  }

  getCurrentVariantId() {
    // Get variant ID from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    
    if (variantId) {
      this.currentVariantId = variantId;
    } else {
      // Fallback to the selected variant from the form
      const variantInput = document.querySelector('input[name="id"]');
      if (variantInput) {
        this.currentVariantId = variantInput.value;
      }
    }
  }

  checkIfTodaySelected() {
    const fulfillmentConstants = window.FulfillmentConstants;
    const dateKey = fulfillmentConstants && fulfillmentConstants.DATE ? fulfillmentConstants.DATE : 'selected_fulfillment_date';
    const storageDate = localStorage.getItem(dateKey);
    this.isTodaySelected = !!(storageDate && window.FulfillmentDateUtils?.isSelectedDateToday?.(storageDate));
  }

  normalizeLocationId(locationId) {
    const raw = (locationId || '').toString().trim();
    if (!raw) return '';
    if (raw.includes('gid://shopify/Location/')) {
      return raw.split('/').pop() || raw;
    }
    return raw;
  }

  listenForDateChanges() {
    // Listen for the fulfillment date changed event
    window.addEventListener('fulfillmentDateChanged', () => {
      this.checkIfTodaySelected();
      this.updateInventoryDisplay();
    });

    // Also listen for direct date selector changes
    const dateSelector = document.getElementById('fulfillment-date-delivery');
    if (dateSelector) {
      dateSelector.addEventListener('change', () => {
        setTimeout(() => {
          this.checkIfTodaySelected();
          this.updateInventoryDisplay();
        }, 100);
      });
    }
  }

  listenForLocationChanges() {
    // Listen for location changes in localStorage
    window.addEventListener('storage', (e) => {
      const fulfillmentConstants = window.FulfillmentConstants;
      const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
      if (e.key === locationKey) {
        this.selectedLocationId = this.normalizeLocationId(e.newValue);
        this.updateInventoryDisplay();
      }
    });

    // Also check for location changes on page load and after location selection
    const constants = window.FulfillmentConstants;
    const initialLocationKey = constants && constants.LOCATION_ID ? constants.LOCATION_ID : 'selected_location_id';
    this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(initialLocationKey));
    
    // Listen for custom location change events
    window.addEventListener('locationChanged', () => {
      const fulfillmentConstants = window.FulfillmentConstants;
      const locationKey = fulfillmentConstants && fulfillmentConstants.LOCATION_ID ? fulfillmentConstants.LOCATION_ID : 'selected_location_id';
      this.selectedLocationId = this.normalizeLocationId(localStorage.getItem(locationKey));
      this.updateInventoryDisplay();
    });
  }

  listenForVariantChanges() {
    // Listen for variant picker changes
    const variantInputs = document.querySelectorAll('input[name="id"]');
    variantInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.currentVariantId = input.value;
        this.updateInventoryDisplay();
      });
    });

    // Also listen for URL changes (for single page apps)
    let currentUrl = window.location.href;
    setInterval(() => {
      if (window.location.href !== currentUrl) {
        currentUrl = window.location.href;
        this.getCurrentVariantId();
        this.updateInventoryDisplay();
      }
    }, 1000);
  }

  async fetchInventoryData() {
    if (!this.selectedLocationId) return null;
    const locationId = this.normalizeLocationId(this.selectedLocationId);
    if (!locationId) return null;

    const requestContext = {
      isTodaySelected: this.isTodaySelected,
      locationId: locationId,
      variantId: this.currentVariantId
    };

    if (!this._inventoryRequests) this._inventoryRequests = new Map();
    const existing = this._inventoryRequests.get(locationId);
    if (existing) return existing;

    this.isLoading = true;
    const promise = (async () => {
      try {
        const key = locationId;
        let data = null;
        if (window.EtInventoryDomain && typeof window.EtInventoryDomain.fetchInventoryForLocation === 'function') {
          data = await window.EtInventoryDomain.fetchInventoryForLocation(key);
        } else {
          const response = await fetch(`${this.API_URL}/location/${encodeURIComponent(String(key))}/inventory`);
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          data = await response.json();
        }
        if (this.normalizeLocationId(this.selectedLocationId) !== requestContext.locationId ||
            this.isTodaySelected !== requestContext.isTodaySelected) {
          return null;
        }
        return data;
      } catch (error) {
        return null;
      } finally {
        this._inventoryRequests.delete(locationId);
        this.isLoading = this._inventoryRequests.size > 0;
      }
    })();
    this._inventoryRequests.set(locationId, promise);
    return promise;
  }

  updateInventoryDisplay() {
    if (!this.isTodaySelected) {
      this.clearInventoryData();
      return;
    }

    if (!this.selectedLocationId) {
      this.clearInventoryData();
      return;
    }

    if (!this.currentVariantId) {
      this.clearInventoryData();
      return;
    }

    // Show loading state
    this.showLoadingState();

    // Fetch inventory data and update product form (guard against stale response)
    const requestContext = {
      isTodaySelected: this.isTodaySelected,
      locationId: this.normalizeLocationId(this.selectedLocationId),
      variantId: this.currentVariantId
    };
    this.fetchInventoryData().then(data => {
      if (requestContext.isTodaySelected !== this.isTodaySelected ||
          requestContext.locationId !== this.normalizeLocationId(this.selectedLocationId) ||
          requestContext.variantId !== this.currentVariantId) {
        this.hideLoadingState();
        return;
      }
      this.inventoryData = data;
      this.updateProductForm();
      this.hideLoadingState();
    }).catch(error => {
      console.error('PDP Inventory Checker: Failed to update inventory display:', error);
      this.hideLoadingState();
    });
  }

  showLoadingState() {
    const productForm = document.querySelector('.product-form');
    if (productForm) {
      productForm.classList.add('inventory-loading');
    }
  }

  hideLoadingState() {
    const productForm = document.querySelector('.product-form');
    if (productForm) {
      productForm.classList.remove('inventory-loading');
    }
  }

  updateProductForm() {
    if (!this.currentVariantId) return;

    // Find inventory data for this variant
    const inventoryInfo = this.findVariantInventory(this.currentVariantId);
    
    if (inventoryInfo) {
      // Update form with inventory data
      this.updateFormWithInventory(inventoryInfo);
    } else {
      this.clearFormInventoryData();
    }
  }

  findVariantInventory(variantId) {
    if (!this.inventoryData || !this.inventoryData.products) {
      return null;
    }

    // Find the product that contains this specific variant
    for (const productId in this.inventoryData.products) {
      const product = this.inventoryData.products[productId];
      
      if (!product.variants) continue;
      
      // Check if this product contains the variant we're looking for
      if (product.variants[variantId]) {
        // Exception: Skip inventory checking for products in "pay-it-forward-collection"
        const isPayItForward = product.collections?.some((collection) =>
          collection.handle === 'pay-it-forward-collection' ||
          collection.handle === 'pay-it-forward'
        );
        if (isPayItForward) {
          return {
            available: 999, // Always show as available
            committed: 0,
            incoming: 0
          };
        }
        
        // Find the To-Go variant within this specific product
        let toGoInventory = null;
        
        for (const variantIdKey in product.variants) {
          const variant = product.variants[variantIdKey];
          
          if (variant && variant.inventory) {
            toGoInventory = variant.inventory;
            break; // Found To-Go variant, no need to continue
          }
        }
        
        // Return To-Go inventory only
        if (toGoInventory) {
          return {
            available: toGoInventory.available || 0,
            committed: 0, // No longer using committed logic
            incoming: toGoInventory.incoming || 0
          };
        }
      }
    }
    
    return null;
  }

  updateFormWithInventory(inventoryInfo) {
    const submitButton = document.querySelector('.product-form__submit');
    const variantInput = document.querySelector('input[name="id"]');
    const productForm = document.querySelector('.product-form');
    
    if (!submitButton || !variantInput || !productForm) return;

    // Add inventory data attributes to the form
    productForm.setAttribute('data-inventory-available', inventoryInfo.available);
    productForm.setAttribute('data-inventory-committed', inventoryInfo.committed);
    productForm.setAttribute('data-inventory-incoming', inventoryInfo.incoming);
    
         // Check if sold out: available <= 0
     const isSoldOut = inventoryInfo.available <= 0;
    productForm.setAttribute('data-inventory-sold-out', isSoldOut);
    
    if (isSoldOut) {
      // Disable the submit button
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('disabled');
      
      // Update button text
      const buttonText = submitButton.querySelector('span');
      if (buttonText) {
        const originalText = buttonText.getAttribute('data-original-text') || buttonText.textContent;
        buttonText.setAttribute('data-original-text', originalText);
        buttonText.textContent = 'Inventory Limit Reached';
      }
      
      // Add sold-out class to form
      productForm.classList.add('sold-out');
      
      // Disable variant input
      variantInput.disabled = true;
      
    } else {
      // Enable the submit button
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-disabled');
      submitButton.classList.remove('disabled');
      
      // Update button text back to normal
      const buttonText = submitButton.querySelector('span');
      if (buttonText) {
        const originalText = buttonText.getAttribute('data-original-text');
        buttonText.textContent = originalText || 'Add to Cart';
        if (originalText) {
          buttonText.removeAttribute('data-original-text');
        }
      }
      
      // Remove sold-out class from form
      productForm.classList.remove('sold-out');
      
      // Enable variant input
      variantInput.disabled = false;
      
      // Show low stock warning if available quantity is low (less than 5)
      if (inventoryInfo.available <= 5 && inventoryInfo.available > 0) {
        // disable low stock warnings per Julie
        // keeping CSS in case this needs to be brought back
        this.hideLowStockWarning();
        // this.showLowStockWarning(inventoryInfo.available);
      } else {
        this.hideLowStockWarning();
      }
      
    }
  }

  showLowStockWarning(quantity) {
    let warningElement = document.querySelector('.low-stock-warning');
    if (!warningElement) {
      const productForm = document.querySelector('.product-form');
      if (productForm) {
        warningElement = document.createElement('div');
        warningElement.className = 'low-stock-warning';
        warningElement.style.cssText = `
          background-color: #fff3cd;
          color: #856404;
          border: 1px solid #ffeaa7;
          font-size: 12px;
          font-weight: 600;
          padding: 8px 12px;
          border-radius: 4px;
          margin: 10px 0;
          text-align: center;
        `;
        productForm.appendChild(warningElement);
      }
    }
    
    if (warningElement) {
      warningElement.textContent = `⚠️ Only ${quantity} left in stock!`;
    }
  }

  hideLowStockWarning() {
    const warningElement = document.querySelector('.low-stock-warning');
    if (warningElement) {
      warningElement.remove();
    }
  }

  clearFormInventoryData() {
    const productForm = document.querySelector('.product-form');
    if (productForm) {
      productForm.removeAttribute('data-inventory-available');
      productForm.removeAttribute('data-inventory-committed');
      productForm.removeAttribute('data-inventory-incoming');
      productForm.removeAttribute('data-inventory-sold-out');
      productForm.classList.remove('sold-out');
    }
    this.hideLowStockWarning();
  }

  clearInventoryData() {
    this.inventoryData = null;
    this.clearFormInventoryData();
  }

  // Public method to manually trigger inventory check
  refreshInventory() {
    this.updateInventoryDisplay();
  }

  // Get current inventory status for the current variant
  getCurrentVariantInventoryStatus() {
    if (!this.isTodaySelected || !this.selectedLocationId || !this.currentVariantId) {
      return null;
    }
    return this.findVariantInventory(this.currentVariantId);
  }

  // Get current status
  getStatus() {
    return {
      isTodaySelected: this.isTodaySelected,
      selectedLocationId: this.selectedLocationId,
      currentVariantId: this.currentVariantId,
      isLoading: this.isLoading,
      hasInventoryData: !!this.inventoryData
    };
  }
}

// Initialize the PDP inventory checker when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.pdpInventoryChecker = new PDPInventoryChecker();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pdpInventoryChecker = new PDPInventoryChecker();
  });
} else {
  window.pdpInventoryChecker = new PDPInventoryChecker();
} 
